import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSiteSettings } from "../context/SiteSettingsContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Truck, Mail, Lock, User, Phone, Loader2, ArrowLeft, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

export default function RegisterPage() {
  const [step, setStep] = useState(1); // 1: form, 2: verification code
  const [emailVerificationEnabled, setEmailVerificationEnabled] = useState(false);
  const [checkingSettings, setCheckingSettings] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: "",
    phone: "",
    role: "customer"
  });
  const [verificationCode, setVerificationCode] = useState("");
  const [loading, setLoading] = useState(false);
  
  const { register, googleLogin } = useAuth();
  const { settings: siteSettings } = useSiteSettings();
  const navigate = useNavigate();

  // Check if email verification is enabled
  useEffect(() => {
    const checkVerificationStatus = async () => {
      try {
        const res = await axios.get(`${API}/auth/email-verification-status`);
        setEmailVerificationEnabled(res.data.enabled);
      } catch (error) {
        console.error("Error checking verification status:", error);
        setEmailVerificationEnabled(false);
      } finally {
        setCheckingSettings(false);
      }
    };
    checkVerificationStatus();
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Direct registration (without email verification)
  const handleDirectRegister = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setLoading(true);

    try {
      const user = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        role: formData.role
      });
      
      toast.success("Registrazione completata!");
      
      if (user.role === "company") {
        navigate("/company", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante la registrazione");
    } finally {
      setLoading(false);
    }
  };

  // Send verification code
  const handleSendVerification = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      toast.error("Le password non coincidono");
      return;
    }

    if (formData.password.length < 6) {
      toast.error("La password deve essere di almeno 6 caratteri");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/send-verification`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        role: formData.role
      });
      
      toast.success("Codice di verifica inviato!");
      
      if (response.data.message.includes("Codice di verifica (solo per test)")) {
        toast.info(response.data.message, { duration: 10000 });
      }
      
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'invio del codice");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyAndRegister = async (e) => {
    e.preventDefault();
    
    if (verificationCode.length !== 6) {
      toast.error("Inserisci il codice a 6 cifre");
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/verify-email`, {
        email: formData.email,
        code: verificationCode
      });
      
      localStorage.setItem("token", response.data.access_token);
      localStorage.setItem("user", JSON.stringify(response.data.user));
      
      toast.success("Registrazione completata!");
      
      if (response.data.user.role === "company") {
        navigate("/company", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
      
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/send-verification`, {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone || null,
        role: formData.role
      });
      
      toast.success("Nuovo codice inviato!");
      
      if (response.data.message.includes("Codice di verifica (solo per test)")) {
        toast.info(response.data.message, { duration: 10000 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore durante l'invio del codice");
    } finally {
      setLoading(false);
    }
  };

  // Choose which submit handler to use
  const handleSubmit = emailVerificationEnabled ? handleSendVerification : handleDirectRegister;

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div className="hidden lg:block lg:flex-1 relative">
        <img
          src="https://images.unsplash.com/photo-1659353741155-e988422cf9ed?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Delivery driver"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-l from-background via-background/50 to-transparent" />
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex flex-col justify-center px-4 sm:px-6 lg:px-20 xl:px-24 bg-background py-12">
        <div className="mx-auto w-full max-w-sm">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 mb-8">
            {siteSettings.logo_url ? (
              <img src={siteSettings.logo_url} alt="Logo" className="h-12 object-contain" />
            ) : (
              <>
                <div className="bg-primary p-2">
                  <Truck className="h-6 w-6 text-primary-foreground" />
                </div>
                <span className="font-bold text-xl tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                  NOLEGGIO<span className="text-primary">FURGONI</span>.IT
                </span>
              </>
            )}
          </Link>

          {step === 1 ? (
            <>
              <h1 className="text-2xl font-bold tracking-tight mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                Crea il tuo account
              </h1>
              <p className="text-muted-foreground text-sm mb-8">
                Hai già un account?{" "}
                <Link to="/login" className="text-primary hover:underline font-medium">
                  Accedi
                </Link>
              </p>

              {/* Google Register */}
              <Button
                type="button"
                variant="outline"
                className="w-full mb-6 py-5"
                onClick={googleLogin}
                data-testid="google-register-btn"
              >
                <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                Continua con Google
              </Button>

              <div className="relative mb-6">
                <div className="absolute inset-0 flex items-center">
                  <span className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs uppercase">
                  <span className="bg-background px-2 text-muted-foreground">Oppure</span>
                </div>
              </div>

              {/* Register Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Role Selection */}
                <div>
                  <Label className="input-label">Tipo di Account</Label>
                  <RadioGroup
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                    className="flex gap-4 mt-2"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="customer" id="customer" data-testid="role-customer" />
                      <Label htmlFor="customer" className="text-sm font-normal cursor-pointer">Cliente</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="company" id="company" data-testid="role-company" />
                      <Label htmlFor="company" className="text-sm font-normal cursor-pointer">Azienda</Label>
                    </div>
                  </RadioGroup>
                  
                  {formData.role === "company" && (
                    <div className="mt-3 p-3 bg-primary/5 border border-primary/20 rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        Per registrarti come azienda, usa il{" "}
                        <Link to="/registra-azienda" className="text-primary font-medium hover:underline">
                          form di registrazione aziendale
                        </Link>
                        {" "}con validazione della Partita IVA.
                      </p>
                    </div>
                  )}
                </div>

                <div>
                  <Label htmlFor="name" className="input-label">Nome Completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Mario Rossi"
                      value={formData.name}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="register-name"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="input-label">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="nome@esempio.it"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10"
                      required
                      data-testid="register-email"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="phone" className="input-label">Telefono (opzionale)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+39 123 456 7890"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10"
                      data-testid="register-phone"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="password" className="input-label">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 pr-10"
                      required
                      data-testid="register-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                <div>
                  <Label htmlFor="confirmPassword" className="input-label">Conferma Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? "text" : "password"}
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10 pr-10"
                      required
                      data-testid="register-confirm-password"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={loading || checkingSettings}
                  data-testid="register-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {emailVerificationEnabled ? "Invio codice in corso..." : "Registrazione in corso..."}
                    </>
                  ) : (
                    emailVerificationEnabled ? "Continua" : "Crea Account"
                  )}
                </Button>
              </form>
            </>
          ) : (
            <>
              {/* Verification Code Step */}
              <button
                onClick={() => setStep(1)}
                className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6"
              >
                <ArrowLeft className="h-4 w-4" />
                Torna indietro
              </button>

              <div className="flex items-center gap-3 mb-6">
                <div className="h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: 'Chivo, sans-serif' }}>
                    Verifica la tua email
                  </h1>
                  <p className="text-muted-foreground text-sm">
                    Codice inviato a <span className="font-medium text-foreground">{formData.email}</span>
                  </p>
                </div>
              </div>

              <form onSubmit={handleVerifyAndRegister} className="space-y-6">
                <div>
                  <Label htmlFor="code" className="input-label">Codice di Verifica</Label>
                  <Input
                    id="code"
                    type="text"
                    placeholder="123456"
                    value={verificationCode}
                    onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-[0.5em] font-mono"
                    maxLength={6}
                    required
                    data-testid="verification-code"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full btn-primary" 
                  disabled={loading || verificationCode.length !== 6}
                  data-testid="verify-submit"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Verifica in corso...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="mr-2 h-4 w-4" />
                      Completa Registrazione
                    </>
                  )}
                </Button>

                <p className="text-center text-sm text-muted-foreground">
                  Non hai ricevuto il codice?{" "}
                  <button
                    type="button"
                    onClick={handleResendCode}
                    className="text-primary hover:underline font-medium"
                    disabled={loading}
                  >
                    Invia di nuovo
                  </button>
                </p>
              </form>
            </>
          )}

          <p className="text-xs text-muted-foreground text-center mt-6">
            Registrandoti accetti i nostri{" "}
            <Link to="#" className="underline hover:text-primary">Termini e Condizioni</Link>{" "}
            e la{" "}
            <Link to="#" className="underline hover:text-primary">Privacy Policy</Link>.
          </p>
        </div>
      </div>
    </div>
  );
}
